import { ActionsContext, ActionsContextInitialValues, ActionPlanMetadata } from "./context";
import { useSavingStatus } from "src/components/Loader/useSavingStatus";
import { useActionPlanData } from "./useActionPlanData";
import { useGetAllActionPlanActions } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetAllActionPlanAction";
import { useGetOneAction } from "./useGetOneAction";
import { useActionScore } from "./useActionScore";
import { useEffect, useState } from "react";
import { useActionPlanOptions } from "./useActionPlanOptions";
import { useCreateActionPlan } from "./useCreateActionPlan";
import { useEntityScores } from "../Scores/useEntityScores";
import { useOlapScoresParams } from "../OlapScores/useOlapScoresParams";
import { useGetCompanyPosts } from "src/api/client-api/post/useGetAllPosts";
import { useCompanyMetadata } from "../Company/Metadata/useCompanyMetadata";
import { ScoreService } from "src/services/ScoreService";
import { useActionPlanEligibleScores } from "src/hooks/useActionPlanEligibleScores";
import { pickFirstSelectorVariation, scoreHasBaseline } from "src/utils/actionPlanScoreFilters";
import { ValidationMessage } from "src/components/QuestionnaireForm/FormProperty/validationTypes";
import { SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";

export type ActionUrls = {
    noActionPlan: () => string;
    planning: () => string;
    trajectory: () => string;
    trajectoryBarChart: () => string;
    trajectoryCards: () => string;
    catalog: () => string;
    catalogList: () => string;
    catalogGroup: (actionId: number) => string;
    catalogGroupItem: (groupId: number, actionId: number) => string;
    editPlanning: (actionId: string) => string;
    editCatalog: (actionId: string) => string;
};

type ActionsProviderProps = {
    children: ReactNode;

    // Can delete SSQ pointer when backend provides us with the relevant information.
    ssqVersion: SsqVersion;
    extraFilterForAction: AxiosParams;
    urls: ActionUrls;
};
export const ActionsProvider = (props: ActionsProviderProps) => {
    const { ssqVersion, urls, extraFilterForAction } = props;

    const { company } = useCompanyMetadata();
    const persistedOptions = useActionPlanOptions();
    const [isEditMode, setIsEditMode] = useState(false);
    const [isFieldValueUpdating, setIsFieldValueUpdating] = useState<boolean>(false);

    const toggleEditMode = () => {
        setIsEditMode(!isEditMode);
    };

    const [validationMessages, setValidationMessages] = useState<ValidationMessage[]>([]);
    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);

    const handleValidationError = (messages: ValidationMessage[]) => {
        setValidationMessages(messages);
        setIsValidationModalOpen(true);
    };

    const { scoresWithVariations, selectedVariations, lastSelectedVariation, selectVariation } = useEntityScores();
    const creationLoadingStatus = useSavingStatus();

    const scoresParams = useOlapScoresParams({
        scores: {
            variations: selectedVariations.query,
        },
    });
    const olapScoresQuery = scoresParams?.query;

    const { posts } = useGetCompanyPosts({
        entityId: company.id,
        extraParams: {
            ssqId: ssqVersion.ssq.id,
        },
        isEnabled: ssqVersion.id !== undefined,
        status: "calculation",
        scoresQuery: olapScoresQuery,
    });

    const { createActionPlan } = useCreateActionPlan({
        urls,
        setSavingStatus: creationLoadingStatus.setSaving,
    });

    const savingState = useSavingStatus();
    const [isOpenActionPlanForm, setOpenActionPlanForm] = useState(false);

    const { actionPlan, isLoading, isFetched, isRefetching } = useGetOneAction({
        params: extraFilterForAction,
        scoresQuery: olapScoresQuery,
    });
    const { data: allAPA } = useGetAllActionPlanActions({
        actionPlanID: actionPlan?.id,
        scoresQuery: olapScoresQuery,
    });

    const lastItem2 = (actionPlan?.simulationSsqPosts ?? []).length - 1;
    const postWithActionPlan = actionPlan?.simulationSsqPosts[lastItem2];

    const allowedScores = useActionPlanEligibleScores();
    const scoresForSelectors = allowedScores.filter(scoreHasBaseline);

    useEffect(() => {
        if (scoresForSelectors.length === 0) {
            return;
        }

        const isCurrentScoreAllowedForSelectors = scoresForSelectors.some(
            (score) => score.id === lastSelectedVariation.scoreId,
        );

        if (isCurrentScoreAllowedForSelectors) {
            return;
        }

        const fallback = pickFirstSelectorVariation(scoresWithVariations, scoresForSelectors);
        if (fallback === undefined) {
            return;
        }

        selectVariation(fallback.scoreId, fallback.variationId);
    }, [lastSelectedVariation.scoreId, scoresForSelectors, scoresWithVariations, selectVariation]);

    const lastItem1 = (actionPlan?.actionPlanSsqPosts ?? []).length - 1;
    const postWithoutActionPlan = actionPlan?.actionPlanSsqPosts[lastItem1];

    const firstPostWithActionPlan = actionPlan?.simulationSsqPosts[0];
    const actionPlanMetadata: ActionPlanMetadata = {
        actionPlanID: actionPlan?.id,
        actionPlanIRI: actionPlan?.["@id"],
        startingPostId: actionPlan?.startSsqPost.id,
        targetPostIdWithoutActionPlan: postWithoutActionPlan?.ssqPost.id,

        firstPostIdWithActionPlan: firstPostWithActionPlan?.id,
        targetPostIdWithActionPlan: postWithActionPlan?.id,

        isGrowthDisabled: actionPlan?.simulationVaryPercentage === undefined,
    };

    const actionScore = useActionScore({
        ssqVersionId: actionPlan?.startSsqPost.ssqVersionId,
        actionPlan,
        engagedActions: allAPA,
        lastSelectedVariation,
    });
    const {
        actionsByCsrTheme,
        actions,
        actionsByParents,
        selectPostsByActionPlanAction,
        isReady: areActionsReady,
    } = actionScore;

    const actionPlanData = useActionPlanData({ actionPlan, scores: scoresWithVariations });

    useEffect(() => {
        scoresParams.state.setQuery({
            scores: {
                ...scoresParams.query.scores,
                variations: ScoreService.getVariationsByLastSelectedVariation(
                    scoresParams.query.scores?.variations,
                    lastSelectedVariation,
                ),
            },
        });
    }, [lastSelectedVariation.scoreId, lastSelectedVariation.variationId]);

    const selectedActionPlan = actionPlanData.find((i) => {
        return i.scoreId === lastSelectedVariation.scoreId && i.variationId === lastSelectedVariation.variationId;
    });

    const actionPlanStats = {
        // Future elligible actions from actionPlan;
        total: (actions ?? []).length,
        engagedActions: (allAPA ?? []).length,
    };

    // const areQuestionsAnswered = (actionPlanAction: ActionPlanAction, elements: PostElement[]): boolean => {
    //     const properties = actionPlanAction.action.ssqProperties;

    //     return properties.every((ssqProperty) => {
    //         const element = elements.find((element) => element.ssqPropertyId === ssqProperty.id);

    //         if (element === undefined) return false;

    //         return element.value !== "";
    //     });
    // };

    const getElligibleStartingPosts = () => {
        const startingPostId = actionPlan?.startSsqPost.id;
        const allPostsOptions = posts?.flatMap((post) => {
            const postCampaign = post.campaign;
            if (postCampaign === undefined) return [];

            // An Elligible Post can only be LOCKED;
            if (post.isLocked !== true && post.id !== startingPostId) return [];

            return [post];
        });

        return allPostsOptions;
    };

    const hasOnlyOneGroup = actionsByParents.catalogList.length === 1;

    const reformattedUrl = {
        ...urls,
        catalog: () => {
            if (hasOnlyOneGroup) {
                const groupId = actionsByParents.catalogList[0].parent.id;
                return urls.catalogGroup(groupId);
            }
            return urls.catalog();
        },
    };

    const items: ActionsContextInitialValues = {
        validationMessages,
        isValidationModalOpen,
        setIsValidationModalOpen,
        handleValidationError,
        actionsByParents,
        areActionsReady,
        actionPlan: actionPlan ?? null,
        actionPlanData,
        selectPostsByActionPlanAction,
        actionPlanMetadata,
        actions,
        actionsByCsrTheme,
        selectedActionPlan,
        isLoading,
        isFetched,
        isRefetching,
        savingStatus: savingState,
        actionPlanSavingStatus: creationLoadingStatus,
        scoresEnabled: allowedScores,
        scoresForSelectors,
        actionPlanStats,
        urls: reformattedUrl,
        isOpenActionPlanForm,
        setOpenActionPlanForm,
        isEditMode,
        toggleEditMode,
        persistedOptions,
        ssqVersion,
        companyPosts: posts,
        getElligibleStartingPosts,
        createActionPlan,
        isFieldValueUpdating,
        setIsFieldValueUpdating,
        olapScoresQuery,
        lastSelectedVariation,
    };

    return <ActionsContext.Provider value={items}>{props.children}</ActionsContext.Provider>;
};
